import type { MdIdentifier } from "@ptsd/uikit";
import { Button, ComposedText, ComposedTextScoreCard } from "@ptsd/uikit";
import { Text, View, YStack } from "tamagui";

import { api } from "~/utils/api";
import { Paywall } from "~/component/feature/paywall/paywall";
import config from "~/config";
import { usePaymentContext } from "~/contexts/PaymentContext";

type State = "success" | "warning" | "neutral" | "danger" | undefined;

export interface ComposedTextProps {
  description: {
    md: string;
    mdDomain: string;
  };
  title: string;
  image: string;
  score: string | number;
  state: string;
  dynamics: {
    state: string;
    description: string;
    text: string;
  };
  isPaywall?: boolean;
  identifier?: MdIdentifier;
}

interface ComposedTextSlicesProps extends ComposedTextProps {
  type: string;
}

export const ComposedTextSlices: React.FC<ComposedTextSlicesProps> = ({
  description,
  title,
  score,
  state,
  dynamics,
  isPaywall,
  type,
}) => {
  const { mutate, data, isLoading } = api.slices.sendPdfToEmail.useMutation();

  return (
    <ComposedText.Container data-id="composed-text-slices">
      <ComposedText.TextContainer data-id="composed-text-slices-text-container">
        <ComposedText.Title data-id="composed-text-slices-title">
          {title}
        </ComposedText.Title>
        <ComposedText.RenderMd md={description.md} />
      </ComposedText.TextContainer>

      <ComposedText.ScoreContainer>
        <ComposedTextScoreCard.Container
          data-id="composed-text-slices-score-container"
          height={"100%"}
          justifyContent="space-around"
          width={"100%"}
        >
          <View justifyContent="center" alignItems="center" gap="$3">
            <View justifyContent="center" alignItems="center" gap="$0.5">
              <ComposedTextScoreCard.Score
                data-id="composed-text-slices-score"
                state={state as State}
              >
                {score}
              </ComposedTextScoreCard.Score>
              <ComposedTextScoreCard.ScoreTitle data-id="composed-text-slices-score-title">
                {title}
              </ComposedTextScoreCard.ScoreTitle>
            </View>
            <ComposedTextScoreCard.Separator />
            <ComposedTextScoreCard.Dynamics
              {...dynamics}
              state={dynamics.state as State}
            />
          </View>
          <YStack width={"100%"}>
            <Paywall enabled={Boolean(isPaywall)} hideCard={true}>
              <ComposedTextScoreCard.DownloadContainer
                flexDirection="column"
                backgroundColor={"$white"}
              >
                <ComposedTextScoreCard.DownloadBtn
                  width={272}
                  href={
                    new URL(`/export/pdf/slice/${type}`, config.API_URL).href
                  }
                >
                  Завантажити результати
                </ComposedTextScoreCard.DownloadBtn>
                <ComposedTextScoreCard.SendMailBtn
                  width={272}
                  onPress={() => mutate({ type })}
                  disabled={Boolean(data) || isLoading}
                >
                  Надіслати на пошту
                </ComposedTextScoreCard.SendMailBtn>
              </ComposedTextScoreCard.DownloadContainer>
            </Paywall>
          </YStack>
        </ComposedTextScoreCard.Container>
      </ComposedText.ScoreContainer>
    </ComposedText.Container>
  );
};

export const ComposedTextSlicesMobile: React.FC<ComposedTextSlicesProps> = ({
  description,
  title,
  score,
  state,
  dynamics,
  type,
  isPaywall,
}) => {
  const { mutate, data, isLoading } = api.slices.sendPdfToEmail.useMutation();

  return (
    <ComposedText.Container
      data-id="composed-text-slices-mobile"
      variant="borderless"
      rounded="none"
      paddingHorizontal={"$3"}
      paddingTop={"$3"}
      paddingBottom={"$3"}
      flexDirection={"column"}
      shadow="none"
      gap={"$3"}
    >
      <ComposedText.Title
        data-id="composed-text-slices-mobile-title"
        textAlign="center"
      >
        {title}
      </ComposedText.Title>

      <View
        gap={"$2"}
        width={"100%"}
        alignItems="center"
        justifyContent="center"
      >
        <ComposedText.ScoreContainer>
          <ComposedTextScoreCard.Container
            data-id="composed-text-slices-mobile-score-container"
            width={"100%"}
            paddingHorizontal={"$3"}
            justifyContent="space-between"
          >
            <View
              justifyContent="center"
              alignItems="center"
              gap="$2"
              width={"100%"}
            >
              <View justifyContent="center" alignItems="center" gap="$0.5">
                <ComposedTextScoreCard.Score
                  data-id="composed-text-slices-mobile-score"
                  font="md"
                  state={state as State}
                >
                  {score}
                </ComposedTextScoreCard.Score>
                <ComposedTextScoreCard.ScoreTitle data-id="composed-text-slices-mobile-score-title">
                  {title}
                </ComposedTextScoreCard.ScoreTitle>
              </View>
              <ComposedTextScoreCard.Separator />
              <ComposedTextScoreCard.Dynamics
                state={dynamics.state as State}
                description={dynamics.description}
                text={dynamics.text}
              />
            </View>
          </ComposedTextScoreCard.Container>
        </ComposedText.ScoreContainer>
        <YStack width={"100%"}>
          <Paywall enabled={Boolean(isPaywall)} hideCard={true}>
            <ComposedTextScoreCard.DownloadContainer
              flexDirection="column"
              backgroundColor={"$white"}
            >
              <ComposedTextScoreCard.DownloadBtn
                width={272}
                href={new URL(`/export/pdf/slice/${type}`, config.API_URL).href}
              >
                Завантажити результати
              </ComposedTextScoreCard.DownloadBtn>
              <ComposedTextScoreCard.SendMailBtn
                width={272}
                onPress={() => mutate({ type })}
                disabled={Boolean(data) || isLoading}
              >
                Надіслати на пошту
              </ComposedTextScoreCard.SendMailBtn>
            </ComposedTextScoreCard.DownloadContainer>
          </Paywall>
        </YStack>
        <ComposedText.RenderMd md={description.md} />
      </View>
    </ComposedText.Container>
  );
};

interface ComposedPaywallProps {
  isPaywall?: boolean;
}

const ComposedTextPaywall: React.FC<ComposedPaywallProps> = ({ isPaywall }) => {
  const { setIsOpened } = usePaymentContext();
  if (!isPaywall) return null;
  return (
    <>
      <View
        padding={32}
        backgroundColor={"#FFF2F6"}
        borderRadius={"$4"}
        borderStyle={"solid"}
        borderColor={"$lightpink-500"}
      >
        <Text
          fontWeight={500}
          fontSize={18}
          textAlign={"center"}
          fontFamily={"$body"}
        >
          Я хочу отримати повні результати та почати піклуватись про себе
        </Text>
        <View alignItems={"center"} paddingTop={16}>
          <Button
            variant="primary"
            onPress={() => setIsOpened(true)}
            size="md"
            rounded="lg"
            maxWidth={300}
          >
            Отримати повні результати
          </Button>
        </View>
      </View>
      <Text
        fontFamily={"$body"}
        fontWeight={400}
        fontSize={16}
        color={"$gray-600"}
        textAlign={"left"}
      >
        Підписка відкриє доступ до розширених результатів: ви побачите повну
        картину свого стану, зможете зрозуміти, які сфери потребують уваги, і
        отримаєте рекомендації, що допоможуть зміцнити свій психологічний
        баланс.
      </Text>
    </>
  );
};

export const ComposedTextMain: React.FC<ComposedTextProps> = ({
  description,
  title,
  isPaywall,
}) => {
  const { mutate, data, isLoading } = api.scores.sendPdfToEmail.useMutation();

  return (
    <ComposedText.Container data-id="composed-text" flexDirection="column">
      <ComposedText.TextContainer>
        <ComposedText.Title data-id="composed-text-title">
          {title}
        </ComposedText.Title>
        <View data-id={"composed-text-description"}>
          <ComposedText.RenderMd md={description.md} />
        </View>
        <ComposedTextPaywall isPaywall={false} />
      </ComposedText.TextContainer>
      <Paywall enabled={Boolean(isPaywall)} hideCard={true}>
        <ComposedTextScoreCard.DownloadContainer>
          <ComposedTextScoreCard.DownloadBtn
            width={272}
            href={new URL("/export/pdf", config.API_URL).href}
          >
            Завантажити результати
          </ComposedTextScoreCard.DownloadBtn>
          <ComposedTextScoreCard.SendMailBtn
            width={272}
            onPress={() => mutate()}
            disabled={data ?? isLoading}
          >
            Надіслати на пошту
          </ComposedTextScoreCard.SendMailBtn>
        </ComposedTextScoreCard.DownloadContainer>
      </Paywall>
    </ComposedText.Container>
  );
};

export const ComposedTextSphere: React.FC<ComposedTextProps> = ({
  description,
  title,
  image,
  score,
  state,
  dynamics,
  identifier,
}) => {
  return (
    <ComposedText.Container data-id="composed-text-sphere-container">
      <ComposedText.TextContainer
        data-id="composed-text-sphere-text-container"
        justifyContent="center"
      >
        <ComposedText.Title data-id="composed-text-sphere-title">
          {title}
        </ComposedText.Title>

        <View data-id="composed-text-sphere-description">
          <ComposedText.RenderMd md={`> [${identifier}]\n ${description.md}`} />
        </View>

        <ComposedText.RenderMd
          data-id="composed-text-domain-description"
          md={description.mdDomain}
        />
      </ComposedText.TextContainer>

      <ComposedText.ScoreContainer>
        <ComposedTextScoreCard.Container
          data-id="composed-text-sphere-score-container"
          paddingVertical={"$4"}
          height={"100%"}
          justifyContent="center"
          width={"100%"}
          gap={"$2"}
        >
          <ComposedTextScoreCard.Image path={image} />
          <View justifyContent="center" alignItems="center" gap="$3">
            <View justifyContent="center" alignItems="center" gap="$0.5">
              <ComposedTextScoreCard.Score
                data-id="composed-text-sphere-score"
                state={state as State}
              >
                {score}
              </ComposedTextScoreCard.Score>
            </View>
            <ComposedTextScoreCard.Separator />
            <ComposedTextScoreCard.Dynamics
              state={dynamics.state as State}
              description={dynamics.description}
              text={dynamics.text}
            />
          </View>
        </ComposedTextScoreCard.Container>
      </ComposedText.ScoreContainer>
    </ComposedText.Container>
  );
};

const ComposedTextPaywallMobile: React.FC<ComposedPaywallProps> = ({
  isPaywall,
}) => {
  const { setIsOpened } = usePaymentContext();
  if (!isPaywall) return null;
  return (
    <>
      <View
        padding={32}
        backgroundColor={"#FFF2F6"}
        borderRadius={"$4"}
        borderStyle={"solid"}
        borderColor={"$lightpink-500"}
      >
        <Text
          fontWeight={500}
          fontSize={18}
          textAlign={"center"}
          fontFamily={"$body"}
        >
          Я хочу отримати повні результати та почати піклуватись про себе
        </Text>
        <View alignItems={"center"} paddingTop={16}>
          <Button
            variant="primary"
            onPress={() => setIsOpened(true)}
            size="md"
            rounded="lg"
            maxWidth={300}
          >
            Отримати повні результати
          </Button>
        </View>
      </View>
      <Text
        fontFamily={"$body"}
        fontWeight={400}
        fontSize={16}
        color={"$gray-600"}
        textAlign={"left"}
      >
        Підписка відкриє доступ до розширених результатів: ви побачите повну
        картину свого стану, зможете зрозуміти, які сфери потребують уваги, і
        отримаєте рекомендації, що допоможуть зміцнити свій психологічний
        баланс.
      </Text>
    </>
  );
};

export const ComposedTextMobile: React.FC<ComposedTextProps> = ({
  description,
  title,
  isPaywall,
}) => {
  const { mutate, data, isLoading } = api.scores.sendPdfToEmail.useMutation();
  return (
    <ComposedText.Container
      variant="borderless"
      rounded="none"
      backgroundColor={"$warm-snow-500"}
      flexDirection={"column"}
      shadow="none"
      gap={"$3"}
      size="sm"
    >
      <ComposedText.Title textAlign="center">{title}</ComposedText.Title>
      <View
        gap={"$2"}
        width={"100%"}
        alignItems="center"
        justifyContent="center"
      >
        <ComposedText.RenderMd md={description.md} />
        <ComposedTextPaywallMobile isPaywall={false} />
        {!isPaywall && (
          <View gap={"$2"} width={"100%"} padding="$2" maxWidth={344}>
            <ComposedTextScoreCard.DownloadBtn
              width={undefined}
              href={new URL("/export/pdf", config.API_URL).href}
            >
              Завантажити результати
            </ComposedTextScoreCard.DownloadBtn>
            <ComposedTextScoreCard.SendMailBtn
              onPress={() => mutate()}
              disabled={data ?? isLoading}
            >
              Надіслати на пошту
            </ComposedTextScoreCard.SendMailBtn>
          </View>
        )}
      </View>
    </ComposedText.Container>
  );
};

export const ComposedTextMobileSphere: React.FC<ComposedTextProps> = ({
  description,
  title,
  score,
  state,
  dynamics,
  image,
}) => {
  return (
    <ComposedText.Container
      variant="borderless"
      rounded="none"
      backgroundColor={"$warm-snow-500"}
      paddingHorizontal={"$3"}
      flexDirection={"column"}
      shadow="none"
      gap={"$3"}
    >
      <ComposedText.Title textAlign="center">{title}</ComposedText.Title>

      <View
        gap={"$2"}
        width={"100%"}
        alignItems="center"
        justifyContent="center"
      >
        <ComposedText.ScoreContainer>
          <ComposedTextScoreCard.Container
            width={300}
            paddingHorizontal={"$3"}
            justifyContent="space-between"
            alignSelf="center"
          >
            <View
              justifyContent="center"
              alignItems="center"
              gap="$2"
              width={"100%"}
            >
              <ComposedTextScoreCard.Image path={image} />

              <View justifyContent="center" alignItems="center" gap="$0.5">
                <ComposedTextScoreCard.Score font="md" state={state as State}>
                  {score}
                </ComposedTextScoreCard.Score>
              </View>
              <ComposedTextScoreCard.Separator />
              <ComposedTextScoreCard.Dynamics
                state={dynamics.state as State}
                description={dynamics.description}
                text={dynamics.text}
              />
            </View>
          </ComposedTextScoreCard.Container>
        </ComposedText.ScoreContainer>

        <ComposedText.RenderMd md={`> [${state}]\n${description.md}`} />
        <ComposedText.RenderMd md={description.mdDomain} />
      </View>
    </ComposedText.Container>
  );
};
